import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { useEntityStore } from '@ContractBuilder/store';
import { useDrawerStore } from '@ContractBuilder/store/drawer.store';
import { useTriggerContractRefresh } from '@hooks/useContractAutoRefresh';
import { useEntityId } from '@hooks/useEntityId';
import { entityQueries } from '@queries/fetchEntity';
import type { AuditEntry } from '@root/@types/audit-log';
import { getAllBlocks } from '@root/helpers/blocks';
import Nav from '@src/routes';
import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash-es';

import { ACTIVITY_POLLING_INTERVAL_FOR_CLONE_JOB } from '../constants';
import { ENDORSEMENT_ACTIONS_SHARED_WITH_SUMMARY } from '../copy';
import { Activity } from '../views/Activity';
import type { ActivitySort } from '../views/ActivityControls';
import { EVERYONE } from '../views/ActivityControls';
import { ActivityEmptyState } from '../views/ActivityEmptyState';
import { ActivityLoading } from '../views/ActivityLoading';

const isEntryVisibleInView = (entry: AuditEntry, isEndorsementSummaryView: boolean): boolean => {
  if (isEndorsementSummaryView) {
    return (
      entry.surface === 'endorsement-summary' ||
      (entry.surface === 'endorsement' &&
        (ENDORSEMENT_ACTIONS_SHARED_WITH_SUMMARY as readonly string[]).includes(entry.action))
    );
  }
  return entry.surface !== 'endorsement-summary';
};

export const ActivityController = () => {
  const { closeDrawer } = useDrawerStore(({ closeDrawer }) => ({ closeDrawer }));
  const { isLoading, submission } = useEntityStore(({ isLoading, submission }) => ({
    isLoading,
    submission,
  }));

  const entityId = useEntityId();
  const triggerRefresh = useTriggerContractRefresh(entityId);
  const isEndorsementSummaryView = !!useMatch(Nav.EndorsementView);
  const blocks = getAllBlocks(submission, isEndorsementSummaryView);
  const sections = (submission?.sections ?? []).map(({ id, label }) => ({ id, label }));

  // Filter + sort are FE-only state: preserved across live refetches (controller stays mounted),
  // reset on reload. Not persisted.
  const [filterKey, setFilterKey] = useState<string>(EVERYONE);
  const [sort, setSort] = useState<ActivitySort>('newest');

  useEffect(() => {
    triggerRefresh();
  }, [triggerRefresh]);

  // Audit query is keyed on updated_at (see entityQueries.auditLog) so a contract change refetches
  // rather than serving a stale frame
  const { data, isLoading: isAuditLoading } = useQuery({
    ...entityQueries.auditLog(entityId, submission?.updated_at),
    refetchInterval: (query) => {
      const auditLog: AuditEntry[] = get(query, 'state.data.audit_log', []);
      const hasRunningCloneJob = auditLog.some(
        (item) => item.action === 'endorsement.duplicated' && item.status === 'RUNNING',
      );

      return hasRunningCloneJob ? ACTIVITY_POLLING_INTERVAL_FOR_CLONE_JOB : false;
    },
  });

  if (isLoading || isAuditLoading || !data?.audit_log) {
    return <ActivityLoading onClose={closeDrawer} />;
  }

  const auditLog = data.audit_log.filter((entry) => isEntryVisibleInView(entry, isEndorsementSummaryView));

  if (!auditLog.length) {
    return <ActivityEmptyState>No activity yet</ActivityEmptyState>;
  }

  return (
    <Activity
      blocks={blocks}
      sections={sections}
      data={auditLog}
      onClose={closeDrawer}
      filterKey={filterKey}
      onFilter={setFilterKey}
      sort={sort}
      onToggleSort={() => setSort((s) => (s === 'newest' ? 'oldest' : 'newest'))}
    />
  );
};
